import { DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import type { Image } from '@core-mkt/interfaces/image';
import { EnvService } from '@core-mkt/services/env/env.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { ProductService } from '@core-mkt/services/product/product.service';
import { ALLSTATE_DEFAULT_DESCRIPTION } from '@core-mkt/services/schema/allstate-roadside.schema';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { takeWhile } from 'rxjs';
import type { Bullet } from './rsa-product';
import { RsaProduct } from './rsa-product';
import { getRsaProductServiceSchema } from './rsa-product.schema';

@Component({
  selector: 'rsa-product',
  templateUrl: './rsa-product.component.html',
  styleUrls: ['./rsa-product.component.scss'],
  standalone: true,
  imports: [FontAwesomeModule, VisionButtonComponent, LazyImgDirective],
})
export class RsaProductComponent implements OnInit {
  @Input() data: RsaProduct;
  private _percentDiscount: string;
  theme: ThemeInterface;
  faCheck = faCheck;
  product: XGritCompleteProduct;
  titleAppend: string;
  defaultTitle = 'when you select 1 FREE month of Allstate Roadside Services.';
  defaultDescription = ALLSTATE_DEFAULT_DESCRIPTION;
  defaultBullets = [
    {
      bulletItem: 'Flat tire service',
    },
    {
      bulletItem: 'Battery service',
    },
    {
      bulletItem: 'Towing',
    },
    {
      bulletItem: 'Fuel delivery',
    },
    {
      bulletItem: 'Lockout service',
    },
  ];
  defaultImage: Image = {
    url: `https://xgrit-ecom.imgix.net/ace/ee9f08bf-bfb3-4dfd-8f1f-ef25747fea4e.png?v=1678723018&auto=compress,format`,
    altText: 'rsa-logo',
  };

  private readonly schemaService = inject(SchemaService);
  private readonly document = inject<Document>(DOCUMENT);

  constructor(
    private ps: ProductService,
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private env: EnvService,
  ) {}

  get regularPrice(): number {
    return this.product?.apiData.originalPrice;
  }

  get promotionPrice(): number {
    return this.product?.apiData.discountPrice;
  }

  get percentDiscount(): string {
    if (this.promotionPrice === 0) return '100%';
    if (this.regularPrice && this.promotionPrice) {
      return this._percentDiscount;
    }
    return '0';
  }

  get backgroundColor(): string {
    return this.data.darkMode ? 'bg-vis-dark text-vis-reverse' : 'bg-gray-100 text-vis-regular';
  }

  get textColorPromotionPrice(): string {
    return this.data.darkMode ? 'text-vis-reverse' : 'text-ace-pink-600';
  }

  get title(): string {
    return this.data.rsaTitle;
  }

  get description(): string {
    return this.data.rsaDescription;
  }

  get bullets(): Bullet[] {
    return this.data.rsaBullets?.length ? this.data.rsaBullets : this.defaultBullets;
  }

  get buttonStyle(): string {
    return this.data.rsaCtaStyle;
  }

  get ctaButtonLabel(): string {
    return this.data.rsaCtaLabel ?? this.product?.apiData.title;
  }

  get ctaButtonLink(): string {
    return this.data.rsaCtaLink ?? this.product?.apiData.checkoutLink;
  }

  get ctaButtonLinkNewTab(): boolean {
    return this.data.rsaCtaLinkNewTab;
  }

  get ctaButtonLinkNofollow(): boolean {
    return this.data.rsaCtaLinkNofollow;
  }

  get ctaButtonCtaTag(): string {
    return this.data.rsaCtaTag;
  }

  get ctaButtonData(): VisionButton {
    return {
      buttonStyle: this.buttonStyle,
      bgTheme: this.backgroundColor,
      buttonText: this.ctaButtonLabel,
      buttonLink: this.ctaButtonLink,
      fullWidth: false,
      nofollow: this.ctaButtonLinkNofollow,
      noreferrer: this.ctaButtonLinkNewTab,
      noopener: this.ctaButtonLinkNewTab,
      newTab: this.ctaButtonLinkNewTab,
      ctaTag: this.ctaButtonCtaTag,
    };
  }

  get hasCta(): boolean {
    return (
      (this.ctaButtonLabel && this.ctaButtonLabel !== null && this.ctaButtonLabel.length > 0) ||
      (this.ctaButtonLink && this.ctaButtonLink !== null && this.ctaButtonLink.length > 0)
    );
  }

  get rsaImageUrl(): string {
    if (this.data.rsaImage[0] && this.data.rsaImage[0].url) {
      if (this.data.rsaImage[0].url?.includes('auto=compress,format')) {
        return this.data.rsaImage[0].url + '&w=459&fit=max';
      }
      return `${this.data.rsaImage[0].url}&auto=compress,format&w=459&fit=max`;
    }
    return this.defaultImage.url;
  }

  get rsaTopButtonText(): string {
    return 'Sign up today and enjoy 1 FREE month of service. Only $5/month after your free trial. Cancel anytime.';
  }

  get rsaTopListText(): string {
    return `${this.env.get.brandConfig.name}'s Exclusive Offer Includes:`;
  }

  get rsaImageAltText(): string {
    if (this.data.rsaImage[0]) {
      return this.data.rsaImage[0].altText;
    }
    return this.defaultImage.altText;
  }
  get rsaImageFooterTextShow(): boolean {
    return this.data.rsaImageFooterTextShow;
  }
  get rsaImageFooterText(): string {
    if (this.data.rsaImageFooterTextShow) {
      return this.data.rsaImageFooterText ? this.data.rsaImageFooterText : this.env.get.brandConfig.rsaImageFooterText;
    }
    return null;
  }

  ngOnInit(): void {
    // Register the Allstate Roadside Services `Service` schema SYNCHRONOUSLY and
    // unconditionally — it must emit on every page rsa-product renders on, including
    // category pages with no top-product (where `setProduct()` is never called). Uses
    // the ORIGINAL Craft title/description before they are mutated/sanitized below.
    this.registerServiceSchema(this.data?.rsaTitle, this.data?.rsaDescription);

    this.setTheme();
    if (this.data.rsaTitle) {
      this.redactor.bypassSanitizer(this.data.rsaTitle);
    } else if (this.ps.isProductValid(this.data.product)) {
      const product = this.ps.extractProduct(this.data.product);
      this.ps.getProductData(product).then((product: XGritCompleteProduct) => {
        this.product = product;
        if (this.promotionPrice && this.regularPrice) {
          this._percentDiscount = Math.round(100 - (this.promotionPrice * 100) / this.regularPrice).toString();
        }
        this.data.rsaTitle = `<${this.data.titleHeader}>Save ${this.percentDiscount}% ${this.defaultTitle} </${this.data.titleHeader}>`;
      });
    } else {
      this.ps.currentData.pipe(takeWhile((x) => x === null || Object.keys(x).length > 1)).subscribe((data): void => {
        if (data) {
          this.product = data;
          if (this.promotionPrice && this.regularPrice) {
            this._percentDiscount = Math.round(100 - (this.promotionPrice * 100) / this.regularPrice).toString();
          }
          this.data.rsaTitle = `<${this.data.titleHeader}>Save ${this.percentDiscount}% ${this.defaultTitle} </${this.data.titleHeader}>`;
        } else {
          this.data.rsaTitle = `<h2>Save 25% when you select 1 FREE month of Allstate Roadside Services.</h2>`;
        }
      });
    }
    this.data.rsaDescription = this.data?.rsaDescription
      ? this.redactor.bypassSanitizer(this.data.rsaDescription)
      : this.defaultDescription;
  }

  /**
   * Registers the Allstate Roadside Services `Service` schema (no-op on browser;
   * SSR-only). Always emits a Service; adds `isRelatedTo: urn:xgrit:course:{productId}`
   * when the related product can be resolved synchronously.
   */
  private registerServiceSchema(title: unknown, description: unknown): void {
    this.schemaService.register(
      getRsaProductServiceSchema({ title, description, productId: this.resolveRelatedProductId() }, this.document.URL),
    );
  }

  /**
   * Resolves the xGrit product id the upsell relates to, synchronously, for the
   * Service `isRelatedTo` Course reference:
   *  1. the rsa-product block's own product, when a config wires one (not in the
   *     default Craft fragment); else
   *  2. the page's product when unambiguous (the page-data resolver pre-fetches it
   *     into `ProductService.pageProducts` before render). `setProduct()` is NOT a
   *     reliable source — only top-product/top-section call it, and asynchronously.
   * Returns null when there is no product or more than one (avoids a wrong reference);
   * the Service still emits, just without `isRelatedTo`.
   */
  private resolveRelatedProductId(): string | null {
    if (this.ps.isProductValid(this.data.product)) {
      return this.ps.extractProduct(this.data.product)?.xgritProductId ?? null;
    }
    const pageProducts = this.ps.pageProducts;
    if (pageProducts?.length === 1) {
      return pageProducts[0]?.craftData?.xgritProductId ?? null;
    }
    return null;
  }

  setTheme(): void {
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.backgroundColor,
      backgroundImage: undefined,
      textColor: '',
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }
}
